'use client';

import { c as _c } from "react/compiler-runtime";
import type { MarketId, MarketParams } from '@morpho-org/blue-sdk';
import type { InterfaceToken, MarketForRewards } from '@morpho-org/dapp-core';
import { LargeAssetInput } from '@morpho-org/ui-v6/client';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { useQueryClient } from '@tanstack/react-query';
import { memo, useCallback, useState } from 'react';
import type { MarketGeoRestriction } from '@/hooks/useMarketGeoRestriction';
import { ActionButtons } from '@/components/common/ActionButtons';
import BundleAuthorizationCallout from '@/components/common/BundleAuthorizationCallout/BundleAuthorizationCallout';
import { MARKET_BUNDLE_NOT_AUTHORIZED_MESSAGE } from '@/components/common/BundleAuthorizationCallout/messages';
import { DISABLED_LEND_TOOLTIP } from '@/components/common/DisabledActionTooltip';
import { ErrorServiceUnavailable } from '@/components/common/Error';
import { TransferError } from '@/components/common/Error/TransferErrors';
import { MarketLendSummaryCard } from '@/components/common/MarketLendSummary';
import { WrapSegment } from '@/components/common/WrapSegment';
import MarketLendWithdrawReviewDialogV2 from '@/components/MarketPage/MarketActionV2/MarketLendWithdrawReviewDialogV2';
import { useWeb3Context } from '@/contexts/Web3Context';
import { getMarketGeoRestrictionActionProps } from '@/helpers/marketGeoRestriction';
import useMarketTooltip from '@/hooks/operation/market/useMarketTooltip';
import { useMarketLendForm } from '@/hooks/operation/market/v2/useMarketLendForm';
import useBundleCanTransferOnchain from '@/hooks/operation/useBundleCanTransferOnchain';
import useFinalizeLabel from '@/hooks/operation/useFinalizeLabel';
import { refetchLendPositionsUntilIndexed } from '@/hooks/positions/refetchLendPositionsUntilIndexed';
import { useTrackMarketActionsV2EntryViewed } from '@/lib/analytics/client/hooks/useTrackMarketActionsV2EntryViewed';
import { trackMarketActionsV2EntryClicked } from '@/lib/analytics/client/trackers/market-actions-v2';
import { buildMarketLendCompletedParams, trackMarketLendCompleted } from '@/lib/analytics/client/trackers/market-lend';
import { getWalletType } from '@/lib/observability/shared/wallet-type';
interface MarketOperationLendProps {
  marketId: MarketId;
  collateralAsset: InterfaceToken;
  loanAsset: InterfaceToken;
  /** morpho-sdk MarketParams for the v2 flow (from the parent's API market). */
  marketParams: MarketParams | undefined;
  marketForRewards?: MarketForRewards | null;
  avgSupplyApy?: number | null;
  /** VRM-5363: restricted-jurisdiction verdict for this market's assets; blocks the CTA. */
  geoRestriction: MarketGeoRestriction;
  /**
   * The panel's one-sided gate has not answered yet. The form still renders so typed input
   * survives, but it cannot be finalized: the wallet could turn out to hold a borrow position.
   */
  isPositionGatePending: boolean;
}

/**
 * Direct-lend panel — supplies the market's loan asset through morpho-sdk `blue.supply`.
 * The borrow-side sister panels own collateral; a lender's form is one input, so this is
 * the loan-asset input, the optional native-wrap toggle, and the lend summary.
 */
const MarketOperationLend = t0 => {
  const $ = _c(86);
  const {
    marketId,
    collateralAsset,
    loanAsset,
    marketParams,
    marketForRewards,
    avgSupplyApy,
    geoRestriction,
    isPositionGatePending
  } = t0;
  const {
    account,
    targetChain
  } = useWeb3Context();
  const {
    toggleDialog
  } = useDialogContext();
  const queryClient = useQueryClient();
  const [isInputPending, setIsInputPending] = useState(false);
  let t1;
  if ($[0] !== loanAsset || $[1] !== marketParams) {
    t1 = {
      marketParams,
      loanAsset
    };
    $[0] = loanAsset;
    $[1] = marketParams;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    isMarketLendEnabled,
    inputLendAmount,
    setInputLendAmountManually,
    isEmpty,
    positionData,
    positionError,
    isPositionLoading,
    currentSupply,
    afterSupply,
    maxLend,
    isMaxActive,
    isMaxLoading,
    onMaxClick,
    isWNativeLoan,
    isWrapping,
    onWrappingChange,
    wrappableNativeBalance,
    nativeAmount,
    isWrappingToggleLoading,
    buildErrorMessage,
    canTransferLoanAsset,
    isBlockedByBorrowPosition,
    isDebouncing,
    isFinalizeDisabled,
    isButtonLoading,
    reset
  } = useMarketLendForm(t1);
  const {
    canTransfer: bundleCanTransfer
  } = useBundleCanTransferOnchain("bundles.blueBundlesV1", loanAsset?.address, targetChain.id);
  let t2;
  if ($[3] !== isMarketLendEnabled || $[4] !== marketId || $[5] !== targetChain.id || $[6] !== targetChain.name) {
    t2 = {
      entryPoint: "market_page",
      action: "lend",
      marketId,
      chainId: targetChain.id,
      chainName: targetChain.name,
      source: "market_page",
      enabled: isMarketLendEnabled
    };
    $[3] = isMarketLendEnabled;
    $[4] = marketId;
    $[5] = targetChain.id;
    $[6] = targetChain.name;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  useTrackMarketActionsV2EntryViewed(t2);
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== account.isWrongChain || $[10] !== buildErrorMessage || $[11] !== isEmpty) {
    t4 = {
      operationLabel: "Lend",
      isInitialState: isEmpty,
      isNew: true,
      isEditing: false,
      localErrorMessage: buildErrorMessage,
      operations: t3,
      isPending: false,
      blockError: null,
      isWrongChain: account.isWrongChain,
      hasDisabledMessage: false,
      noReviewPrefix: true
    };
    $[9] = account.isWrongChain;
    $[10] = buildErrorMessage;
    $[11] = isEmpty;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const finalizeLabel = useFinalizeLabel(t4);
  const tooltipContent = useMarketTooltip(isBlockedByBorrowPosition ? DISABLED_LEND_TOOLTIP : buildErrorMessage);
  const t5 = isFinalizeDisabled || isPositionGatePending || !bundleCanTransfer;
  let t6;
  if ($[13] !== finalizeLabel || $[14] !== geoRestriction || $[15] !== isButtonLoading || $[16] !== t5 || $[17] !== tooltipContent) {
    t6 = getMarketGeoRestrictionActionProps({
      geoRestriction,
      isFinalizeDisabled: t5,
      isLoading: isButtonLoading,
      finalizeLabel,
      tooltipContent
    });
    $[13] = finalizeLabel;
    $[14] = geoRestriction;
    $[15] = isButtonLoading;
    $[16] = t5;
    $[17] = tooltipContent;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let geoActionProps;
  let shouldBlockFinalize;
  if ($[19] !== t6) {
    ({
      shouldBlockFinalize,
      ...geoActionProps
    } = t6);
    $[19] = t6;
    $[20] = geoActionProps;
    $[21] = shouldBlockFinalize;
  } else {
    geoActionProps = $[20];
    shouldBlockFinalize = $[21];
  }
  let t7;
  if ($[22] !== account.address || $[23] !== account.isContract || $[24] !== account.isSafe || $[25] !== avgSupplyApy || $[26] !== collateralAsset || $[27] !== inputLendAmount || $[28] !== loanAsset || $[29] !== marketForRewards || $[30] !== marketId || $[31] !== marketParams || $[32] !== nativeAmount || $[33] !== queryClient || $[34] !== reset || $[35] !== shouldBlockFinalize || $[36] !== targetChain.id || $[37] !== targetChain.name || $[38] !== toggleDialog) {
    t7 = () => {
      if (!marketParams || shouldBlockFinalize) {
        return;
      }
      trackMarketActionsV2EntryClicked({
        entryPoint: "market_page",
        action: "lend",
        marketId,
        chainId: targetChain.id,
        chainName: targetChain.name,
        source: "market_page"
      });
      toggleDialog(<MarketLendWithdrawReviewDialogV2 marketParams={marketParams} mode="lend" amount={inputLendAmount} nativeAmount={nativeAmount} collateralAsset={collateralAsset} loanAsset={loanAsset} marketForRewards={marketForRewards} avgSupplyApy={avgSupplyApy} onTxSuccess={lendAmount => {
        const params = buildMarketLendCompletedParams({
          marketId,
          loanAsset,
          lendAmount,
          chainId: targetChain.id,
          chainName: targetChain.name,
          walletType: getWalletType(account.isSafe, account.isContract),
          entryPoint: "market_page",
          source: "market_page"
        });
        if (params) {
          trackMarketLendCompleted(params);
        }
        queryClient.invalidateQueries();
        refetchLendPositionsUntilIndexed(queryClient, account.address, {
          chainId: targetChain.id,
          marketId
        });
        reset();
      }} entryPoint="market_page" />);
    };
    $[22] = account.address;
    $[23] = account.isContract;
    $[24] = account.isSafe;
    $[25] = avgSupplyApy;
    $[26] = collateralAsset;
    $[27] = inputLendAmount;
    $[28] = loanAsset;
    $[29] = marketForRewards;
    $[30] = marketId;
    $[31] = marketParams;
    $[32] = nativeAmount;
    $[33] = queryClient;
    $[34] = reset;
    $[35] = shouldBlockFinalize;
    $[36] = targetChain.id;
    $[37] = targetChain.name;
    $[38] = toggleDialog;
    $[39] = t7;
  } else {
    t7 = $[39];
  }
  const handleFinalize = t7;
  if (positionError && !positionData) {
    let t8;
    if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <ErrorServiceUnavailable minHeight="600px" />;
      $[40] = t8;
    } else {
      t8 = $[40];
    }
    return t8;
  }
  const t8 = `Lend ${loanAsset.symbol}`;
  let t9;
  if ($[41] !== inputLendAmount || $[42] !== isMaxActive || $[43] !== isMaxLoading || $[44] !== loanAsset || $[45] !== maxLend || $[46] !== onMaxClick || $[47] !== setInputLendAmountManually || $[48] !== t8) {
    t9 = <LargeAssetInput header={t8} token={loanAsset} amount={inputLendAmount} onChange={setInputLendAmountManually} onPendingChange={setIsInputPending} maxValue={maxLend} isMaxLoading={isMaxLoading} isMax={isMaxActive} onMaxClick={onMaxClick} autoFocus={true} />;
    $[41] = inputLendAmount;
    $[42] = isMaxActive;
    $[43] = isMaxLoading;
    $[44] = loanAsset;
    $[45] = maxLend;
    $[46] = onMaxClick;
    $[47] = setInputLendAmountManually;
    $[48] = t8;
    $[49] = t9;
  } else {
    t9 = $[49];
  }
  let t10;
  if ($[50] !== isMarketLendEnabled || $[51] !== isWNativeLoan || $[52] !== isWrapping || $[53] !== isWrappingToggleLoading || $[54] !== loanAsset.symbol || $[55] !== onWrappingChange || $[56] !== targetChain.nativeCurrency.symbol || $[57] !== wrappableNativeBalance) {
    t10 = <WrapSegment id="market-lend" enabled={isMarketLendEnabled} isWNative={isWNativeLoan} wrappableNativeBalance={wrappableNativeBalance} wrappedSymbol={loanAsset.symbol} nativeSymbol={targetChain.nativeCurrency.symbol} isWrapping={isWrapping} onWrappingChange={onWrappingChange} disabled={isWrappingToggleLoading} />;
    $[50] = isMarketLendEnabled;
    $[51] = isWNativeLoan;
    $[52] = isWrapping;
    $[53] = isWrappingToggleLoading;
    $[54] = loanAsset.symbol;
    $[55] = onWrappingChange;
    $[56] = targetChain.nativeCurrency.symbol;
    $[57] = wrappableNativeBalance;
    $[58] = t10;
  } else {
    t10 = $[58];
  }
  let t11;
  if ($[59] !== canTransferLoanAsset || $[60] !== loanAsset.address) {
    t11 = !canTransferLoanAsset && <TransferError tokenAddress={loanAsset.address} />;
    $[59] = canTransferLoanAsset;
    $[60] = loanAsset.address;
    $[61] = t11;
  } else {
    t11 = $[61];
  }
  let t12;
  if ($[62] !== t10 || $[63] !== t11 || $[64] !== t9) {
    t12 = <div className="flex flex-col gap-[2px]">{t9}{t10}{t11}</div>;
    $[62] = t10;
    $[63] = t11;
    $[64] = t9;
    $[65] = t12;
  } else {
    t12 = $[65];
  }
  let t13;
  if ($[66] !== afterSupply || $[67] !== avgSupplyApy || $[68] !== currentSupply || $[69] !== isPositionLoading || $[70] !== loanAsset || $[71] !== marketForRewards || $[72] !== marketId) {
    t13 = <MarketLendSummaryCard marketId={marketId} currentSupply={currentSupply} afterSupply={afterSupply} loanAsset={loanAsset} isLoading={isPositionLoading} marketForRewards={marketForRewards} avgSupplyApy={avgSupplyApy} />;
    $[66] = afterSupply;
    $[67] = avgSupplyApy;
    $[68] = currentSupply;
    $[69] = isPositionLoading;
    $[70] = loanAsset;
    $[71] = marketForRewards;
    $[72] = marketId;
    $[73] = t13;
  } else {
    t13 = $[73];
  }
  let t14;
  if ($[74] !== bundleCanTransfer) {
    t14 = !bundleCanTransfer && <BundleAuthorizationCallout variant="destructive" message={MARKET_BUNDLE_NOT_AUTHORIZED_MESSAGE} />;
    $[74] = bundleCanTransfer;
    $[75] = t14;
  } else {
    t14 = $[75];
  }
  let t15;
  if ($[76] !== geoActionProps || $[77] !== handleFinalize || $[78] !== isDebouncing || $[79] !== isInputPending) {
    t15 = <ActionButtons handleFinalize={handleFinalize} {...geoActionProps} isInputPending={isInputPending} isValidating={isDebouncing} />;
    $[76] = geoActionProps;
    $[77] = handleFinalize;
    $[78] = isDebouncing;
    $[79] = isInputPending;
    $[80] = t15;
  } else {
    t15 = $[80];
  }
  let t16;
  if ($[81] !== t12 || $[82] !== t13 || $[83] !== t14 || $[84] !== t15) {
    t16 = <>{t12}{t13}{t14}{t15}</>;
    $[81] = t12;
    $[82] = t13;
    $[83] = t14;
    $[84] = t15;
    $[85] = t16;
  } else {
    t16 = $[85];
  }
  return t16;
};
export default memo(MarketOperationLend);